/**
 * Comprehensive UnitCo Session Reset Utility
 * Handles all possible UnitCo caching mechanisms
 */

export const resetUnitCoSession = async (): Promise<void> => {
  try {
    console.log('[resetUnitCoSession] Starting comprehensive Unit.co reset...');
    
    // 1. Clear all UnitCo DOM elements (more aggressive)
    const unitElements = document.querySelectorAll('unit-elements-white-label-app, unit-elements-activity, unit-elements-account-card, [data-unit], [class*="unit"], [id*="unit"]');
    console.log('[resetUnitCoSession] Found', unitElements.length, 'Unit.co elements to remove');
    unitElements.forEach(el => {
      try {
        // Try to disconnect/dispose before removing
        if ('disconnectedCallback' in el && typeof (el as any).disconnectedCallback === 'function') {
          (el as any).disconnectedCallback();
        }
        // Remove all attributes
        while (el.attributes.length > 0) {
          el.removeAttribute(el.attributes[0].name);
        }
        el.remove();
      } catch (e) {
        console.warn('[resetUnitCoSession] Error removing element:', e);
        el.remove(); // Force remove even if cleanup fails
      }
    });

    // 2. Clear UnitCo iframes
    const unitIframes = document.querySelectorAll('iframe[src*="unit"], iframe[src*="ui.unit.co"], iframe[src*="ui.unit.sh"]');
    unitIframes.forEach(iframe => {
      iframe.remove();
    });

    // 3. Clear UnitCo scripts (but keep the main SDK script for reuse)
    const unitScripts = document.querySelectorAll('script[src*="unit"], script[id*="unit"]');
    console.log('[resetUnitCoSession] Found', unitScripts.length, 'Unit.co scripts');
    unitScripts.forEach(scriptEl => {
      const script = scriptEl as HTMLScriptElement;
      // Don't remove the main SDK script - we'll reuse it
      const scriptId = script.getAttribute('id');
      if (scriptId !== 'unit-wl-script') {
        script.remove();
      } else {
        // Clear any event handlers on the main script
        script.onload = null;
        script.onerror = null;
      }
    });

    // 4. Clear UnitCo global variables and custom element definitions
    const globalVars = ['UnitCo', 'unit', 'UnitElements', 'unitElements'];
    globalVars.forEach(varName => {
      if ((window as any)[varName]) {
        console.log('[resetUnitCoSession] Clearing global variable:', varName);
        delete (window as any)[varName];
      }
    });
    
    // Try to unregister custom elements (if possible)
    if (window.customElements) {
      const customElementNames = ['unit-elements-white-label-app', 'unit-elements-activity', 'unit-elements-account-card'];
      customElementNames.forEach(name => {
        try {
          if (window.customElements.get(name)) {
            console.log('[resetUnitCoSession] Custom element registered:', name, '- cannot unregister, but will recreate');
          }
        } catch (e) {
          // Custom elements cannot be unregistered, that's fine
        }
      });
    }

    // 5. Clear UnitCo service workers
    if (navigator.serviceWorker) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      registrations.forEach(reg => {
        if (reg.active?.scriptURL.includes('unit')) {
          reg.unregister();
        }
      });
    }

    // 6. Clear UnitCo localStorage (all possible keys)
    const unitKeys = Object.keys(localStorage).filter(key => 
      key.toLowerCase().includes('unit') || 
      key.toLowerCase().includes('co') ||
      key.startsWith('unit_') ||
      key.startsWith('unitco_')
    );
    unitKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    // 7. Clear UnitCo sessionStorage
    const unitSessionKeys = Object.keys(sessionStorage).filter(key => 
      key.toLowerCase().includes('unit') || 
      key.toLowerCase().includes('co') ||
      key.startsWith('unit_') ||
      key.startsWith('unitco_')
    );
    unitSessionKeys.forEach(key => {
      sessionStorage.removeItem(key);
    });

    // 8. Clear UnitCo cookies (all domains)
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      if (name.toLowerCase().includes('unit') || name.toLowerCase().includes('co')) {
        
        // Clear for all possible domains and paths
        const domains = [
          window.location.hostname,
          `.${window.location.hostname}`,
          '.unit.sh',
          '.unit.co',
          'unit.sh',
          'unit.co'
        ];
        
        const paths = ['/', '/banking', '/dashboard'];
        
        domains.forEach(domain => {
          paths.forEach(path => {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain}`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain};secure`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain};samesite=none`;
          });
        });
      }
    });

    // 9. Force garbage collection if available
    if ((window as any).gc) {
      (window as any).gc();
    }

    // 10. Clear any remaining UnitCo-related data
    const allElements = document.querySelectorAll('*');
    allElements.forEach(el => {
      try {
        if (el.getAttribute('data-unit') || (el.className && typeof el.className === 'string' && el.className.includes('unit'))) {
          el.removeAttribute('data-unit');
          if (el.className && typeof el.className === 'string') {
            el.className = el.className.replace(/unit[^\\s]*/g, '');
          }
        }
      } catch (error) {
        // Skip elements that don't have className property or have issues
      }
    });

    console.log('[resetUnitCoSession] ✅ Unit.co session reset complete');
  } catch (error) {
    console.error('❌ Error during UnitCo session reset:', error);
    throw error; // Re-throw to allow caller to handle
  }
};

/**
 * Force complete page reload with UnitCo reset
 */
export const forceUnitCoReload = (): void => {
  
  // Clear everything before reload
  resetUnitCoSession().then(() => {
    // Add a small delay to ensure cleanup completes
    setTimeout(() => {
      window.location.href = window.location.pathname + '?reset=' + Date.now();
    }, 100);
  });
};
